import * as React from "react";
import { Text } from "@uikit";
import { Course, CourseClosure } from "@kalabo/golfsavers-common";
import { StarRating } from "@components";
import { getCourseDaysOfOperation } from "@kalabo/golfsavers-common";
import LikeShare from "@components/common/likeShare";

interface IProps {
  course: Course;
  course_closures: CourseClosure[];
  onShowMap: () => void;
  onShowReviews: () => void;
}

const CourseTitle = ({
  course,
  course_closures,
  onShowMap,
  onShowReviews,
}: IProps) => {
  const getPriceRange = () => {
    switch (course?.price_range?.toLowerCase()) {
      case "premium":
        return "$$$$";
      case "expensive":
        return "$$$";
      case "moderate":
        return "$$";
      case "budget":
        return "$";
      default:
        return "";
    }
  };

  const isPermanentClosure = React.useMemo(() => {
    if (!course_closures) return false;
    return course_closures.some((closure: CourseClosure) => {
      return closure.reason === "CLOSED_PERMANENTLY";
    });
  }, [course_closures]);

  const isClosedUntilFurtherNotice = React.useMemo(() => {
    if (!course_closures) return false;
    return course_closures.some((closure: CourseClosure) => {
      return (
        closure.reason === "CLOSED_FURTHER_NOTICE" && closure.end_date === null
      );
    });
  }, [course_closures]);

  const isClosedUntilDate = React.useMemo(() => {
    if (!course_closures) return false;
    return course_closures.some((closure: CourseClosure) => {
      return (
        closure.reason === "CLOSED_FURTHER_NOTICE" && closure.end_date !== null
      );
    });
  }, [course_closures]);

  return (
    <div className="mt-5 sm:mt-0 mb-5 sm:mb-6">
      <div className="grid grid-cols-12">
        <div className="col-span-12 sm:col-span-12">
          <div className="flex items-center mb-[5px] justify-between">
            <div>
              <Text
                size={25}
                sizeTablet={30}
                sizeDesktop={35}
                weight="semibold"
                element="h1"
                leading={1.2}
              >
                {course.name.en}
              </Text>
            </div>
            <div className="hidden sm:block">
              <LikeShare value={course?.slug} name={course?.name?.en} />
            </div>
          </div>
          {!isPermanentClosure && (
            <div className="flex flex-row md:items-center">
              <div className="flex items-center">
                <StarRating
                  mode="small"
                  rating={course.reviews.average_rating}
                  size="medium"
                  countSize={15}
                />
                <Text
                  size={15}
                  color="black90"
                  weight="semibold"
                  className="ml-1"
                >
                  ·
                </Text>

                <span
                  onClick={() => {
                    if (course.reviews.count > 0) onShowReviews();
                  }}
                >
                  <Text
                    size={15}
                    color="black90"
                    weight="normal"
                    className={`ml-1  ${
                      course.reviews.count > 0 &&
                      "hover:text-blue cursor-pointer underline font-semibold"
                    } }`}
                  >
                    {course.reviews.count > 0
                      ? `${course.reviews.count} review${
                          course.reviews.count > 1 ? "s" : ""
                        }`
                      : `No reviews yet`}
                  </Text>
                </span>

                {course.is_recommended && (
                  <div className="self-center bg-darkorange pt-[2px] pb-[2px] px-[6px]  sm:hidden rounded-[2px]  ml-[10px]">
                    <Text
                      size={12}
                      weight="semibold"
                      leading={1.3}
                      color="white"
                      className="uppercase"
                    >
                      Recommended
                    </Text>
                  </div>
                )}
                <span className="mx-[10px] h-4 w-[1px] bg-grey4"></span>
              </div>
              <div className="flex items-center sm:ml-0">
                <Text size={15} color="black90" className="self-center">
                  <span className="hidden lg:inline">
                    {course.holes}-hole championship golf course in{" "}
                    {course.region.name.en}
                  </span>{" "}
                  <span className="mx-[6px] hidden lg:inline">·</span>
                </Text>
                <Text size={15} color="black90" className="self-center">
                  {getPriceRange()}{" "}
                </Text>
                {!isClosedUntilFurtherNotice && (
                  <>
                    <Text
                      size={15}
                      color="black90"
                      className="self-center hidden sm:inline"
                    >
                      <span className="mx-[6px]">·</span>
                      {getCourseDaysOfOperation(course.days_of_operation)}
                    </Text>{" "}
                  </>
                )}
                <span
                  onClick={() => {
                    onShowMap();
                  }}
                  className="hidden sm:inline"
                >
                  <Text
                    size={15}
                    color="black90"
                    weight="normal"
                    className="ml-1"
                  >
                    ·{" "}
                    <span className="underline font-semibold  cursor-pointer hover:text-blue">
                      View on map
                    </span>
                  </Text>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseTitle;
